import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { authService, type LoginRequest, type RegisterRequest } from '@/features/auth/authService';
import { extractApiError } from '@/services/apiClient';
import { tokenStore } from '@/services/tokenStore';
import type { AuthUser, UserRole } from '@/types/domain';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasRole: (...roles: UserRole[]) => boolean;
  login: (payload: LoginRequest) => Promise<AuthUser>;
  register: (payload: RegisterRequest) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /** Silent session rehydration: try `/auth/me` which the axios client will
   * silently refresh the token for if needed. If both fail we land logged out. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await authService.me();
        if (!cancelled) setUser(me);
      } catch (err) {
        // 401 with no refresh cookie = anonymous visitor (expected)
        const apiError = extractApiError(err);
        if (apiError && apiError.status !== 401) {
          // log but don't surface — failed rehydration shouldn't block the public UI
          console.warn('[auth] rehydration failed', apiError);
        }
        tokenStore.clear();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Keep React user state in sync with the token store. When the access
   * token is cleared *outside* an explicit logout — e.g. a mid-session
   * refresh failure inside the axios interceptor — drop the user so
   * ProtectedRoute redirects to login instead of trusting a stale session.
   * Token *sets* (login / silent refresh) don't touch user here; that's
   * owned by login()/register()/rehydration. */
  useEffect(() => {
    return tokenStore.subscribe((token) => {
      if (token === null) setUser((prev) => (prev ? null : prev));
    });
  }, []);

  const login = useCallback(async (payload: LoginRequest) => {
    const next = await authService.login(payload);
    setUser(next);
    return next;
  }, []);

  const register = useCallback(async (payload: RegisterRequest) => {
    const next = await authService.register(payload);
    setUser(next);
    return next;
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
  }, []);

  const hasRole = useCallback(
    (...roles: UserRole[]) => !!user && roles.some((r) => user.roles.includes(r)),
    [user],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        hasRole,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// useAuth is intentionally co-located with its AuthProvider (idiomatic context
// pattern); Fast Refresh's component-only-export rule is a known false positive
// here, so it's disabled for this single export.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
